import localforage from 'localforage';
import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';

// Initialize localforage
const sermonStore = localforage.createInstance({
  name: 'sermonbuild',
  storeName: 'sermons'
});

const syncQueueStore = localforage.createInstance({
  name: 'sermonbuild',
  storeName: 'syncQueue'
});

// Interface for offline sermon data
export interface OfflineSermon {
  id: string;
  title: string;
  scripture: string;
  content: string;
  status: 'draft' | 'complete';
  series_id: string | null;
  date: string | null;
  youtube_url: string | null;
  version: number;
  lastModified: number; // timestamp
  pendingSync: boolean;
}

// Interface for sync queue items
interface SyncQueueItem {
  id: string;
  operation: 'create' | 'update';
  data: OfflineSermon;
  timestamp: number;
  retryCount: number;
}

// Save sermon to local storage
export async function saveSermonOffline(sermonData: Partial<OfflineSermon> & { id: string }): Promise<OfflineSermon> {
  try {
    // Get existing sermon if it exists
    const existingSermon = await sermonStore.getItem<OfflineSermon>(sermonData.id);
    
    // Create new sermon data with updated fields
    const updatedSermon: OfflineSermon = {
      ...(existingSermon || {
        id: sermonData.id,
        title: '',
        scripture: '',
        content: '',
        status: 'draft',
        series_id: null,
        date: null,
        youtube_url: null,
        version: 0,
        lastModified: Date.now(),
        pendingSync: true
      }),
      ...sermonData,
      lastModified: Date.now(),
      pendingSync: true
    };
    
    // Save to local storage
    await sermonStore.setItem(sermonData.id, updatedSermon);
    
    // Add to sync queue
    await addToSyncQueue(updatedSermon);
    
    return updatedSermon;
  } catch (error) {
    console.error('Error saving sermon offline:', error);
    throw error;
  }
}

// Load sermon from local storage
export async function loadSermonOffline(id: string): Promise<OfflineSermon | null> {
  try {
    return await sermonStore.getItem<OfflineSermon>(id);
  } catch (error) {
    console.error('Error loading sermon offline:', error);
    return null;
  }
}

// Add sermon to sync queue
async function addToSyncQueue(sermon: OfflineSermon): Promise<void> {
  try {
    const operation = sermon.version <= 1 ? 'create' : 'update';
    
    const queueItem: SyncQueueItem = {
      id: sermon.id,
      operation,
      data: sermon,
      timestamp: Date.now(),
      retryCount: 0
    };
    
    await syncQueueStore.setItem(sermon.id, queueItem);
  } catch (error) {
    console.error('Error adding to sync queue:', error);
  }
}

// Process sync queue
export async function processSyncQueue(): Promise<void> {
  if (!navigator.onLine) {
    console.log('Offline, skipping sync');
    return;
  }
  
  try {
    // Get all items from sync queue
    const keys = await syncQueueStore.keys();
    
    if (keys.length === 0) {
      return;
    }
    
    console.log(`Processing sync queue: ${keys.length} items`);
    
    for (const key of keys) {
      const queueItem = await syncQueueStore.getItem<SyncQueueItem>(key);
      
      if (!queueItem) continue;
      
      try {
        // Check if we're online
        if (!navigator.onLine) {
          console.log('Connection lost during sync, will retry later');
          break;
        }
        
        // Get the current user session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.log('No active session, skipping sync');
          break;
        }
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.log('No user found, skipping sync');
          break;
        }
        
        const { id, operation, data: sermonData } = queueItem;
        
        // Prepare sermon data for Supabase
        const supabaseData = {
          title: sermonData.title,
          scripture: sermonData.scripture,
          content: sermonData.content,
          status: sermonData.status,
          series_id: sermonData.series_id,
          date: sermonData.date,
          youtube_url: sermonData.youtube_url,
          version: sermonData.version,
          updated_at: new Date().toISOString()
        };
        
        let result;
        
        if (operation === 'create') {
          // Check if sermon already exists (might have been created in another session)
          const { data: existingSermon } = await supabase
            .from('sermons')
            .select('id')
            .eq('id', id)
            .maybeSingle();
          
          if (existingSermon) {
            // Sermon exists, update instead
            result = await supabase
              .from('sermons')
              .update(supabaseData)
              .eq('id', id)
              .eq('user_id', user.id);
          } else {
            // Create new sermon
            result = await supabase
              .from('sermons')
              .insert([{
                id,
                user_id: user.id,
                ...supabaseData,
                created_at: new Date().toISOString()
              }]);
          }
        } else {
          // Update existing sermon
          result = await supabase
            .from('sermons')
            .update(supabaseData)
            .eq('id', id)
            .eq('user_id', user.id);
        }
        
        if (result.error) {
          console.error(`Error syncing sermon ${id}:`, result.error);
          
          // If we've tried too many times, give up
          if (queueItem.retryCount >= 5) {
            console.log(`Too many retries for sermon ${id}, removing from queue`);
            await syncQueueStore.removeItem(id);
          } else {
            // Increment retry count
            await syncQueueStore.setItem(id, {
              ...queueItem,
              retryCount: queueItem.retryCount + 1
            });
          }
        } else {
          console.log(`Successfully synced sermon ${id}`);
          
          // Update local storage to mark as synced
          const sermon = await sermonStore.getItem<OfflineSermon>(id);
          if (sermon) {
            await sermonStore.setItem(id, {
              ...sermon,
              pendingSync: false
            });
          }
          
          // Remove from sync queue
          await syncQueueStore.removeItem(id);
        }
      } catch (error) {
        console.error(`Error processing sync item ${key}:`, error);
        
        // If we've tried too many times, give up
        if (queueItem.retryCount >= 5) {
          console.log(`Too many retries for sermon ${key}, removing from queue`);
          await syncQueueStore.removeItem(key);
        } else {
          // Increment retry count
          await syncQueueStore.setItem(key, {
            ...queueItem,
            retryCount: queueItem.retryCount + 1
          });
        }
      }
    }
  } catch (error) {
    console.error('Error processing sync queue:', error);
  }
}

// Get all sermons from local storage
export async function getAllOfflineSermons(): Promise<OfflineSermon[]> {
  try {
    const sermons: OfflineSermon[] = [];
    await sermonStore.iterate<OfflineSermon, void>((value) => {
      sermons.push(value);
    });
    return sermons;
  } catch (error) {
    console.error('Error getting all offline sermons:', error);
    return [];
  }
}

// Check if there are pending syncs
export async function hasPendingSync(): Promise<boolean> {
  try {
    const keys = await syncQueueStore.keys();
    return keys.length > 0;
  } catch (error) {
    console.error('Error checking pending syncs:', error);
    return false;
  }
}

// Get count of pending syncs
export async function getPendingSyncCount(): Promise<number> {
  try {
    const keys = await syncQueueStore.keys();
    return keys.length;
  } catch (error) {
    console.error('Error getting pending sync count:', error);
    return 0;
  }
}

// Initialize network status listeners
let syncInterval: NodeJS.Timeout | null = null;

export function initOfflineSupport(): void {
  // Set up online/offline event listeners
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // Check connection status immediately
  if (navigator.onLine) {
    handleOnline();
  } else {
    handleOffline();
  }
}

// Clean up network status listeners
export function cleanupOfflineSupport(): void {
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);
  
  if (syncInterval) {
    clearInterval(syncInterval);
    syncInterval = null;
  }
}

// Handle coming online
function handleOnline(): void {
  console.log('Connection restored, syncing data...');
  
  // Process sync queue immediately
  processSyncQueue();
  
  // Set up interval to process sync queue
  if (!syncInterval) {
    syncInterval = setInterval(() => {
      processSyncQueue();
    }, 60000); // Check every minute
  }
}

// Handle going offline
function handleOffline(): void {
  console.log('Connection lost, enabling offline mode');
  
  // Clear sync interval
  if (syncInterval) {
    clearInterval(syncInterval);
    syncInterval = null;
  }
}